import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "../../api";
import { TRIAL } from "../../constants/testIds";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^(?:\+?91)?[6-9]\d{9}$/;
const EMPTY_FORM = { gym_name: "", owner_name: "", phone: "", email: "", city: "", website: "" };

function validate(form) {
  const errs = {};
  if (!form.gym_name.trim()) errs.gym_name = "Gym name is required";
  if (!form.owner_name.trim()) errs.owner_name = "Owner name is required";
  if (!PHONE_RE.test(form.phone.replace(/[\s-]/g, ""))) errs.phone = "Enter a valid 10-digit Indian mobile number";
  if (!EMAIL_RE.test(form.email.trim())) errs.email = "Enter a valid email address";
  return errs;
}

// UX-TRIAL §1: public, unauthenticated signup surface mounted directly on the login page
// ("on/near the login page" per spec — not a separate marketing route). `website` is the
// invisible honeypot field (§2A — real humans never see/fill it, a bot filling every field
// trips it and is silently never provisioned); `formRenderedAt` is captured the instant THIS
// component mounts so the backend can soft-flag a sub-2.5s submission as bot-like timing
// (§2A minimum-time-on-form) without ever hard-rejecting a fast-but-genuine autofill.
export function TrialSignupForm({ onBackToLogin }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [apiError, setApiError] = useState("");
  const [result, setResult] = useState(null);
  const [formRenderedAt, setFormRenderedAt] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { setFormRenderedAt(Date.now()); }, []);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setApiError("");
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/trial/signup", { ...form, form_rendered_at: String(formRenderedAt) });
      // A genuinely-held signup gets a real preview-session cookie set server-side on this SAME
      // response, signaled by `preview_available` (the raw session id itself never leaves the
      // httpOnly cookie) — a honeypot-tripped submission returns an identical-looking "held" body
      // but no session at all (the bot must never learn it was rejected). Navigating to the
      // preview is now the applicant's own choice (see the result screen below), not automatic.
      setResult(data);
    } catch (e2) {
      setApiError(formatApiError(e2).message);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="space-y-4">
        <p data-testid={TRIAL.resultMessage} className="text-sm" style={{ color: "rgba(255,255,255,0.85)" }}>
          {result.message || "You're in — full access unlocked."}
        </p>
        {result.status === "approved" && (
          <p className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>
            We've emailed <span className="font-semibold">{result.login_email}</span> your login details — free, full access, no expiry.
          </p>
        )}
        {result.preview_available && (
          <div className="rounded-[14px] p-4 space-y-2.5" style={{ background: "linear-gradient(135deg,rgba(214,32,43,0.16),rgba(255,90,70,0.10))", border: "1px solid rgba(214,32,43,0.35)" }}>
            <p className="text-sm font-semibold text-white">Curious how it looks in the meantime?</p>
            <p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>
              We've set you up with a live look around — real members, billing, the works, using our demo gym's data. Only if you want to.
            </p>
            <button
              type="button"
              data-testid={TRIAL.explorePreviewButton}
              onClick={() => navigate("/app/dashboard")}
              className="text-sm font-semibold px-3.5 py-2 rounded-[10px] text-white hover:scale-[1.02] transition-transform"
              style={{ background: "linear-gradient(135deg,#D6202B,#FF5A46)" }}
            >
              Take a look around →
            </button>
          </div>
        )}
        <button
          type="button"
          data-testid={TRIAL.continueButton}
          onClick={onBackToLogin}
          className="text-sm font-semibold hover:opacity-80 transition-opacity"
          style={{ color: "#FF4436" }}
        >
          Back to log in →
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3.5" noValidate>
      <p className="text-sm mb-1" style={{ color: "rgba(255,255,255,0.7)" }}>
        Full access, completely free — no card, no expiry. Our team reviews every application and sets your gym up shortly after.
      </p>
      <SignupField label="Gym name" required testId={TRIAL.gymNameInput} value={form.gym_name} onChange={set("gym_name")} error={errors.gym_name} />
      <SignupField label="Owner name" required testId={TRIAL.ownerNameInput} value={form.owner_name} onChange={set("owner_name")} error={errors.owner_name} />
      <SignupField label="Mobile number" required testId={TRIAL.phoneInput} type="tel" value={form.phone} onChange={set("phone")} error={errors.phone} />
      <SignupField label="Email" required testId={TRIAL.emailInput} type="email" value={form.email} onChange={set("email")} error={errors.email} />
      <SignupField label="City (optional)" testId={TRIAL.cityInput} value={form.city} onChange={set("city")} />

      {/* §2A honeypot — visually hidden off-screen (not display:none, which some bots skip);
          a real user never tabs into or sees this field. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
        <label htmlFor="trial-website-field">Website</label>
        <input
          id="trial-website-field"
          data-testid={TRIAL.honeypotInput}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={set("website")}
        />
      </div>

      <button
        type="submit"
        data-testid={TRIAL.submitButton}
        disabled={busy}
        className="w-full py-2.5 rounded-[12px] text-sm font-semibold text-white flex items-center justify-center gap-2 transition-transform disabled:opacity-70 hover:scale-[1.01] active:scale-[0.99] mt-1"
        style={{ background: "#D6202B", boxShadow: "0 8px 22px rgba(214, 32, 43,0.35)" }}
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : "Get free access"}
      </button>

      {apiError && (
        <p data-testid={TRIAL.error} className="text-sm font-medium" style={{ color: "#FF7C62" }}>{apiError}</p>
      )}

      <button
        type="button"
        data-testid={TRIAL.backToLoginLink}
        onClick={onBackToLogin}
        className="text-sm font-medium hover:opacity-80 transition-opacity"
        style={{ color: "#FF4436" }}
      >
        Already have an account? Log in
      </button>
    </form>
  );
}

function SignupField({ label, testId, value, onChange, type = "text", error, required = false }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: "rgba(255,255,255,0.55)" }}>
        {label} {required && <span style={{ color: "#FF7C62" }}>*</span>}
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={onChange}
        aria-invalid={!!error}
        className={`w-full px-3.5 py-2.5 rounded-[10px] text-sm outline-none transition-shadow text-white placeholder:text-white/30 ${error ? "shake-on-error" : ""}`}
        style={{
          border: error ? "1px solid #FF7C62" : "1px solid rgba(255,255,255,0.16)",
          background: "rgba(255,255,255,0.06)",
          boxShadow: error ? "0 0 0 3px rgba(255, 124, 98,0.15)" : "none",
        }}
      />
      {error && <p data-testid={`${testId}-error`} className="text-xs font-medium" style={{ color: "#FF7C62" }}>{error}</p>}
    </div>
  );
}
